import { DeleteOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  Input,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import {
  api,
  type EventSpeakerWithProfile,
  type SlotRow,
  type SlotSourceType,
} from '../lib/api';
import type { PresenceInfo } from '../scenes/EventScene';

const { Title, Text } = Typography;

export function SlotCardEmpty({
  slot,
  availableSpeakers,
  presence,
  onVMixChange,
  onSpeakerChange,
  onDelete,
  onChanged,
}: {
  slot: SlotRow;
  availableSpeakers: EventSpeakerWithProfile[];
  presence: Record<string, PresenceInfo>;
  onVMixChange: (vmix: string) => Promise<void>;
  onSpeakerChange: (speakerId: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onChanged: () => Promise<void> | void;
}) {
  const [vmixForm] = Form.useForm<{ vmix: string }>();
  const [zoomForm] = Form.useForm<{ zoom_meeting_id: string; zoom_passcode: string }>();
  const source: SlotSourceType = slot.source_type ?? 'vmix_call';
  const hasSource =
    source === 'zoom' ? Boolean(slot.zoom_meeting_id) : Boolean(slot.vmix_id);

  const switchSource = async (next: SlotSourceType) => {
    try {
      await api(`/api/slots/${slot.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ source_type: next }),
      });
      await onChanged();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to switch source');
    }
  };

  const saveZoom = async ({
    zoom_meeting_id,
    zoom_passcode,
  }: {
    zoom_meeting_id: string;
    zoom_passcode: string;
  }) => {
    try {
      await api(`/api/slots/${slot.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          zoom_meeting_id: zoom_meeting_id || null,
          zoom_passcode: zoom_passcode || null,
        }),
      });
      await onChanged();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to save Zoom details');
    }
  };

  return (
    <Card
      style={{
        borderColor: '#E5EAF0',
        borderStyle: 'dashed',
        background: '#fafbfc',
      }}
      bodyStyle={{ padding: 16 }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Title level={5} style={{ margin: 0, color: '#0c3643' }}>
            Slot {slot.order_index + 1}
          </Title>
          <Popconfirm title="Remove slot?" okText="Remove" onConfirm={onDelete}>
            <Button size="small" type="text" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>

        <Segmented
          block
          value={source}
          options={[
            { value: 'vmix_call', label: 'vMix Call' },
            { value: 'zoom', label: 'Zoom' },
          ]}
          onChange={(v) => void switchSource(v as SlotSourceType)}
        />

        {source === 'vmix_call' ? (
          <Form
            form={vmixForm}
            initialValues={{ vmix: slot.vmix_id ?? '' }}
            onFinish={({ vmix }) => onVMixChange(vmix)}
            layout="inline"
          >
            <Form.Item name="vmix" style={{ flex: 1, marginRight: 8 }}>
              <Input placeholder="vMix ID" />
            </Form.Item>
            <Form.Item>
              <Button htmlType="submit" type="primary">
                Set
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form
            form={zoomForm}
            initialValues={{
              zoom_meeting_id: slot.zoom_meeting_id ?? '',
              zoom_passcode: slot.zoom_passcode ?? '',
            }}
            onFinish={saveZoom}
            layout="vertical"
          >
            <Form.Item name="zoom_meeting_id" label="Meeting ID" style={{ marginBottom: 8 }}>
              <Input placeholder="e.g. 123 4567 8901" />
            </Form.Item>
            <Form.Item name="zoom_passcode" label="Passcode" style={{ marginBottom: 8 }}>
              <Input placeholder="optional" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button htmlType="submit" type="primary">
                Save Zoom details
              </Button>
            </Form.Item>
          </Form>
        )}

        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Assign speaker
          </Text>
          <Select
            placeholder="Choose speaker"
            style={{ width: '100%', marginTop: 4 }}
            value={undefined}
            onChange={(speakerId: string) => onSpeakerChange(speakerId)}
            options={availableSpeakers.map((s) => {
              const status = presence[s.speaker_id]?.status ?? 'offline';
              const dot = status === 'online' ? '● ' : status === 'hidden' ? '◐ ' : '○ ';
              return {
                label: `${dot}${s.name}`,
                value: s.speaker_id,
              };
            })}
            disabled={!hasSource}
          />
          {!hasSource && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {source === 'zoom'
                ? 'Add the Zoom Meeting ID first.'
                : 'Set vMix ID first.'}
            </Text>
          )}
        </div>
      </Space>
    </Card>
  );
}
